import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import getSessionsImageFiles from 'src/utils/getSessionsImageFiles';

import useGetRetinalExamDataModelId from './useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from './useHandleHttpRequestError';

export default function useGetSessions() {
  const { deviceDataModelId } = useGetRetinalExamDataModelId();

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { setTotalLeftImageAttempt, setTotalRightImageAttempt } =
    useRemoteBoundStore();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const getSessions = useCallback(
    async (iid: string) => {
      try {
        const sessions = await Api.remote.find_retake_sessions(
          deviceDataModelId,
          iid,
        );

        if (sessions.length === 0) {
          setTotalLeftImageAttempt(0);
          setTotalRightImageAttempt(0);
          return;
        }

        const { leftImageFileContainer, rightImageFileContainer } =
          getSessionsImageFiles(sessions);

        setTotalLeftImageAttempt(leftImageFileContainer.length);
        setTotalRightImageAttempt(rightImageFileContainer.length);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: `Failed to find retake sessions. ${errorMessage}`,
          locale: language,
          type: 'error',
        });
      }
    },
    [
      deviceDataModelId,
      handleHttpRequestError,
      language,
      setTotalLeftImageAttempt,
      setTotalRightImageAttempt,
      t,
    ],
  );

  return {
    getSessions,
  };
}
